/**
 * PICKLIST PREFILL NORMALISATION — record value shapes onto schema option values.
 * ===============================================================================
 *
 * THE DEFECT THIS FIXES (field feedback 10, a BLOCKER): a technician is blocked on a field that is
 * visibly already filled. The mechanism is measured and written down in the prefill catalog
 * (`src/forms/prefill.ts`, the `intervention.dispatchReason` caveat): the record stores
 * `LostNetworkConnection` / `PowerOutage` — `Intervention_Sub_Type__c` is an UNRESTRICTED picklist —
 * while the maintenance form's dispatch-reason options say `'Lost Network Connection'` /
 * `'Power Outage'`. `createState` seeds the record's value verbatim, the `single` branch of
 * `checkField` runs `!field.options.includes(chosen)`, and the field fails as **invalid**
 * (`notAnOption`), not as missing — which is why re-picking the same-looking value clears it.
 *
 * THE FIX, per that caveat: normalise the record's value onto the schema's option spelling, and
 * **drop anything that does not map**. Two rules are absolute:
 *
 *  · **Never coerce an unknown value onto an option.** An off-list value prefilled into a
 *    single-choice question is worse than a blank, and several of these land on restricted
 *    Salesforce picklists. A value that maps onto no option is removed so the question is simply
 *    asked.
 *  · **Never relax `validate.ts`.** Validation keeps rejecting off-list values; this module makes
 *    sure a prefill is on-list or absent before validation ever sees it.
 *
 * WHY THIS IS SCHEMA-AWARE rather than a flat value map: the same catalog key feeds fields whose
 * option vocabularies differ per schema. Measured examples, each recorded on the schema field's own
 * `maps` note:
 *
 *  · `kiosk.generation` — the record holds `2.0 | 2.5 | 3.0 | GEN_3.0 | 4.0 | 5.0`; the install and
 *    provisioning forms offer `'3.0'`, the maintenance form offers `'3'`. One input value must land
 *    on a different spelling per schema.
 *  · `intervention.lockerType` — the record holds `Commercial | Residential`; install and
 *    maintenance offer exactly those words, the post-install form offers
 *    `'Retail Locker'` / `'Apartment Locker (Hub)'` for the same two concepts (its own field says
 *    "Prefill must map across all three [vocabularies]").
 *  · `kiosk.mountType` — OpportunityLineItem stores `FREE_STANDING` / `BACK_TO_BACK`; the forms say
 *    `'Free Standing'` / `'Back to Back'`.
 *  · `kiosk.color` — OpportunityLineItem spells `Gray`, Work_Submission__c and the form spell
 *    `Grey`.
 *
 * So normalisation runs per (source key × the option-bearing fields of THE SCHEMA BEING OPENED),
 * in `FormHost`, after the three producers are merged and before `resolvePrefill`.
 *
 * MATCHING IS DETERMINISTIC AND CLOSED, in three steps per candidate value:
 *  1. exact match against the field's options;
 *  2. collapsed match — case, whitespace and punctuation-insensitive (`LostNetworkConnection` ≡
 *     `Lost Network Connection`, `FREE_STANDING` ≡ `Free Standing`). The matched OPTION's spelling
 *     is adopted, never the record's;
 *  3. explicit per-key aliases for the mappings that are semantic rather than mechanical
 *     (`Gray` → `Grey`; `Commercial` → `Retail Locker` on the schema that uses those words).
 * Anything that survives none of the three is dropped for that key. Nothing is ever invented.
 *
 * ONE DOCUMENTED RESIDUAL: within the maintenance schema both `dispatch_reason_core` (8 options)
 * and `dispatch_reason_odin` (17 options; a superset of core's) bind the same key, gated CORE/ODIN
 * by context. A value is kept when it maps onto AT LEAST ONE binding field, so an ODIN-only reason
 * (`IPC Down`) on a kiosk the platform derivation calls CORE still seeds the (visible) core field
 * off-list and validation flags it. That is contradictory record data — an ODIN-specific dispatch
 * reason on a CORE kiosk — surfaced exactly as `validate.ts`'s header says impossible record values
 * should be. The mass defect (every camel-cased value failing on every job) is what this removes.
 *
 * Untrusted sources are left untouched: `createState` never adopts them as answers, and rewriting
 * them here would silently alter the reference value shown beside the question.
 */

import { allFields, isTrustedSource } from '@/forms';
import type { Field, FormSchema, PrefillInputs } from '@/forms';

/** Case, whitespace and punctuation-insensitive comparison form. */
function collapse(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/**
 * Explicit aliases, keyed by catalog key then by COLLAPSED record value, for mappings a mechanical
 * comparison cannot make. Every target string must be a real option spelling on at least one
 * binding field — the match against `field.options` below is what enforces that, so a stale alias
 * degrades to a drop, never to an off-list seed.
 */
const ALIASES: Readonly<Record<string, Readonly<Record<string, readonly string[]>>>> = {
  // OpportunityLineItem spells it "Gray"; Work_Submission__c and the install form spell it "Grey".
  'kiosk.color': { gray: ['Grey'] },
  // The record's formula yields Commercial | Residential. Install and maintenance use those words
  // (step 1 matches them directly); the post-install occupancy field is a third vocabulary for the
  // same two concepts, per its own label: "Residential includes apartment style buildings. Retail
  // includes commercial businesses".
  'intervention.lockerType': {
    commercial: ['Retail Locker'],
    residential: ['Apartment Locker (Hub)'],
  },
};

/**
 * Candidate spellings for one record value, most literal first.
 *
 * The generation variants are here because `Locker_Generation__c` value sets disagree across
 * objects (`GEN_3.0` / `3.0` / `3` all denote the same hardware): the `gen` prefix is stripped and
 * a trailing `.0` is offered both removed and added, so `GEN_3.0` can land on `'3.0'` (install)
 * or `'3'` (maintenance), whichever the schema offers. No candidate is ever adopted unless it
 * matches one of the field's own options.
 */
function candidates(key: string, raw: string): string[] {
  const out: string[] = [raw];
  const push = (v: string): void => {
    if (v !== '' && !out.includes(v)) out.push(v);
  };

  const unprefixed = raw.replace(/^gen[\s_-]*/i, '');
  push(unprefixed);
  for (const base of [raw, unprefixed]) {
    if (/^\d+(\.\d+)?$/.test(base)) {
      push(base.replace(/\.0$/, ''));
      if (!base.includes('.')) push(`${base}.0`);
    }
  }

  for (const alias of ALIASES[key]?.[collapse(raw)] ?? []) push(alias);
  return out;
}

/** The option the value maps onto for this field, in the OPTION's own spelling — or undefined. */
function matchOption(field: Field, key: string, raw: string): string | undefined {
  const options = field.options ?? [];
  for (const candidate of candidates(key, raw)) {
    const exact = options.find((o) => o === candidate);
    if (exact !== undefined) return exact;
    const collapsed = collapse(candidate);
    const loose = options.find((o) => collapse(o) === collapsed);
    if (loose !== undefined) return loose;
  }
  return undefined;
}

/** Whether a field constrains its answer to an option list a seeded value must belong to. */
function isOptionField(field: Field): boolean {
  return (field.kind === 'single' || field.kind === 'multi') && (field.options?.length ?? 0) > 0;
}

/**
 * Normalises the merged prefill inputs against the schema about to be opened.
 *
 * For every trusted string input whose key is bound by at least one option-bearing field of
 * `schema`: rewrite the value to the option spelling it maps onto, or remove it when it maps onto
 * none. Keys bound only by text-shaped fields (or by nothing) pass through untouched, as do
 * non-string values and untrusted sources.
 */
export function normalisePicklistPrefills(inputs: PrefillInputs, schema: FormSchema): PrefillInputs {
  const bindings = new Map<string, Field[]>();
  for (const { field } of allFields(schema)) {
    const source = field.prefill?.source;
    if (source === undefined || !isOptionField(field)) continue;
    const list = bindings.get(source);
    if (list) list.push(field);
    else bindings.set(source, [field]);
  }

  const out: Record<string, PrefillInputs[string]> = {};
  for (const [key, value] of Object.entries(inputs)) {
    const fields = bindings.get(key);
    if (fields === undefined || typeof value !== 'string' || !isTrustedSource(key)) {
      out[key] = value;
      continue;
    }

    const mapped = fields
      .map((field) => matchOption(field, key, value))
      .filter((m): m is string => m !== undefined);

    if (mapped.length === 0) continue; // unmapped: DROPPED, the question is asked

    // Prefer the record's own spelling when it is itself a valid option somewhere; otherwise the
    // first mapping in document order. Distinct spellings for one key within one schema do not
    // occur today (the per-vocabulary cases differ per SCHEMA, not within one).
    out[key] = mapped.includes(value) ? value : mapped[0];
  }
  return out;
}
